import { useEffect, useState, type RefObject } from 'react';
import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { token } from '@atlaskit/tokens';
import { PARTICIPANT_DND_TYPE } from '../../../common/constants';
import type { ParticipantDNDProps, ParticipantStatuses } from '../../../common/types';

type DraggableState =
	| { type: 'idle' }
	| { type: 'preview'; container: HTMLElement }
	| { type: 'dragging' };

const idleState: DraggableState = { type: 'idle' };
const draggingState: DraggableState = { type: 'dragging' };

export const useParticipantDragAndDrop = ({
	participantRef,
	dragHandleRef,
	status,
	id,
	name,
}: {
	participantRef: RefObject<HTMLDivElement>;
	dragHandleRef: RefObject<HTMLButtonElement>;
	status: ParticipantStatuses;
	id: string;
	name: string;
}) => {
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [draggableState, setDraggableState] = useState<DraggableState>(idleState);

	useEffect(() => {
		const dragElement = participantRef.current;
		const dragHandle = dragHandleRef.current;

		const data: ParticipantDNDProps = {
			type: PARTICIPANT_DND_TYPE,
			status,
			id,
			name,
		};

		if (!dragElement || !dragHandle) {
			return;
		}

		/*
		See pragmatic-drag-and-drop docs for details on implementation
		https://atlassian.design/components/pragmatic-drag-and-drop/core-package/adapters/element/about

		Mostly adapted from the list example https://atlassian.design/components/pragmatic-drag-and-drop/examples#list
		*/
		return combine(
			draggable({
				element: dragHandle,
				getInitialData: () => data,
				onGenerateDragPreview({ nativeSetDragImage }) {
					setCustomNativeDragPreview({
						nativeSetDragImage,
						getOffset: pointerOutsideOfPreview({
							x: token('space.200', '16px'),
							y: token('space.100', '8px'),
						}),
						render({ container }) {
							setDraggableState({ type: 'preview', container });

							return () => setDraggableState(draggingState);
						},
					});
				},
				onDragStart() {
					setDraggableState(draggingState);
				},
				onDrop() {
					setDraggableState(idleState);
				},
			}),
			dropTargetForElements({
				element: dragElement,
				canDrop({ source }) {
					if (source.element === dragElement) {
						return false;
					}
					return source.data.type === PARTICIPANT_DND_TYPE;
				},
				getData({ input, element }) {
					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges: ['top', 'bottom'],
					});
				},
				onDrag({ self, source }) {
					if (source.element === dragElement) {
						setClosestEdge(null);
						return;
					}

					setClosestEdge(extractClosestEdge(self.data));
				},
				onDragLeave() {
					setClosestEdge(null);
				},
				onDrop() {
					setClosestEdge(null);
				},
			}),
		);
	}, [name, status, id, participantRef, dragHandleRef]);

	return { closestEdge, draggableState };
};
